/**
 * Single-player setup, written as an operation order: theatre, belligerents, rules — and a typed
 * briefing on the right that summarises the match before `startSinglePlayer(config, speed)`.
 * The theatre is either generated from the seed (any size, 2–6 nations) or a historical map of
 * Europe (docs/Theatres.md) with its own nations: the player picks which one to command.
 */
import {
  type AiDifficulty,
  FACTION_COLORS,
  type FactionSetup,
  isTheatreId,
  loadTheatreMap,
  type MapSize,
  type Season,
  type SessionConfig,
  THEATRES,
  type TheatreId,
} from '@friction/engine';
import type { GameSpeed } from '@friction/protocol';
import { useEffect, useState } from 'react';
import { usePersistentState } from '../../app/usePersistentState';
import { cssColor } from '../../game/format';
import { useGame } from '../../game/store';
import { useT } from '../../i18n';
import { nationName, theatreTitle } from '../../i18n/names';
import { startSinglePlayer } from '../../net/lobby';
import { Button, cx, Field, Rule, Segmented, Select, Slider, TextInput } from '../common/controls';
import { Icon } from '../common/Icon';
import { Paper, Stamp } from '../common/Panel';
import {
  DAY_LIMITS,
  DEFAULT_THEATRE,
  dayLimitLabel,
  difficulties,
  mapOptions,
  mapSizeInfo,
  ROMAN,
  seasonOptions,
  speeds,
  TEAM_NAMES,
  TECH_PRESET_TECHS,
  type TechPreset,
  teamOptions,
  techPresets,
} from './presets';
import { ScreenFrame } from './ScreenFrame';
import s from './screens.module.css';
import { TheatrePreview } from './TheatrePreview';

interface Opponent {
  difficulty: AiDifficulty;
  team: number;
}

interface SpSetup {
  /** 'generated' or a real-world theatre. */
  map: 'generated' | TheatreId;
  /** Theatre mode: the nation the player commands, and each nation's AI quality and team. */
  you: number;
  levels: AiDifficulty[];
  nationTeams: number[];
  mapSize: MapSize;
  opponents: Opponent[];
  teams: 'ffa' | 'custom';
  myTeam: number;
  seedMode: 'random' | 'fixed';
  seed: number;
  dayLimit: number;
  vpShare: number;
  techs: TechPreset;
  speed: GameSpeed;
  season: Season | 'random';
}

const DEFAULT_SETUP: SpSetup = {
  map: DEFAULT_THEATRE,
  you: 0,
  levels: ['veteran', 'veteran', 'veteran', 'veteran', 'veteran', 'veteran'],
  nationTeams: [...(THEATRES[DEFAULT_THEATRE].alliances ?? [0, 1, 2, 3, 4, 5])],
  mapSize: 'medium',
  opponents: [
    { difficulty: 'veteran', team: 1 },
    { difficulty: 'veteran', team: 2 },
  ],
  teams: THEATRES[DEFAULT_THEATRE].alliances ? 'custom' : 'ffa',
  myTeam: 0,
  seedMode: 'random',
  seed: 1924,
  dayLimit: 0,
  vpShare: 0.6,
  techs: 'none',
  speed: 2,
  season: THEATRES[DEFAULT_THEATRE].season,
};

/** How long to wait for the single-player host before telling the player something is wrong. */
const LAUNCH_TIMEOUT_MS = 10_000;

/**
 * Start a single-player match and watch for a host that never answers: `launching` while the
 * worker spins up, `timedOut` when no view arrived within LAUNCH_TIMEOUT_MS. Shared with the
 * scenario briefings.
 */
export function useLaunch(): {
  launching: boolean;
  timedOut: boolean;
  launch(config: SessionConfig, speed: GameSpeed): void;
} {
  const [launching, setLaunching] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const started = useGame((g) => g.view !== null);
  useEffect(() => {
    if (!launching || started) return;
    const t = window.setTimeout(() => setTimedOut(true), LAUNCH_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [launching, started]);
  return {
    launching: launching && !timedOut,
    timedOut,
    launch(config, speed) {
      setTimedOut(false);
      setLaunching(true);
      startSinglePlayer(config, speed);
    },
  };
}

export function MapSizeGlyph({ size }: { size: MapSize }) {
  const rings = size === 'small' ? 1 : size === 'medium' ? 2 : 3;
  const hexes: [number, number][] = [];
  for (let q = -rings; q <= rings; q++)
    for (let r = Math.max(-rings, -q - rings); r <= Math.min(rings, -q + rings); r++) hexes.push([q, r]);
  const R = 3.6;
  return (
    <svg className={s.sizeGlyph} width="38" height="34" viewBox="-19 -17 38 34" aria-hidden>
      {hexes.map(([q, r]) => {
        const x = Math.sqrt(3) * R * (q + r / 2);
        const y = 1.5 * R * r;
        const pts = Array.from({ length: 6 }, (_, i) => {
          const a = (Math.PI / 180) * (60 * i - 30);
          return `${(x + R * 0.92 * Math.cos(a)).toFixed(2)},${(y + R * 0.92 * Math.sin(a)).toFixed(2)}`;
        }).join(' ');
        return (
          <polygon
            key={`${q},${r}`}
            points={pts}
            fill="currentColor"
            fillOpacity={q === 0 && r === 0 ? 0.9 : 0.35}
          />
        );
      })}
    </svg>
  );
}

export function SinglePlayerSetup() {
  const t = useT();
  const [cfg, setCfg] = usePersistentState<SpSetup>('friction.spSetup.v2', DEFAULT_SETUP);
  const patch = (p: Partial<SpSetup>) => setCfg((c) => ({ ...c, ...p }));
  const { launching, timedOut, launch: start } = useLaunch();
  const sizes = mapSizeInfo(t);
  const levels = difficulties(t);
  const seasons = seasonOptions(t);
  const tempo = speeds(t);
  const presets = techPresets(t);
  const teams = teamOptions(t);

  // Settings saved by an older build lack the theatre fields.
  const theatre = isTheatreId(cfg.map) ? THEATRES[cfg.map] : null;
  const nations = theatre?.nations ?? [];
  const you = theatre ? Math.min(cfg.you ?? 0, nations.length - 1) : 0;
  const levelOf = (i: number): AiDifficulty => cfg.levels?.[i] ?? 'veteran';
  const nationTeam = (i: number): number => cfg.nationTeams?.[i] ?? i;
  const factionCount = theatre ? nations.length : cfg.opponents.length + 1;
  const teamList = theatre
    ? nations.map((_, i) => nationTeam(i))
    : [cfg.myTeam, ...cfg.opponents.map((o) => o.team)];
  const teamsUsed = new Set(teamList);
  const teamsInvalid = cfg.teams === 'custom' && teamsUsed.size < 2;
  const setAt = <T,>(list: readonly T[] | undefined, i: number, v: T, fill: T): T[] => {
    const out = Array.from({ length: 6 }, (_, k) => list?.[k] ?? fill);
    out[i] = v;
    return out;
  };
  // A theatre starts with its historical alliances (players may change them).
  const chooseMap = (map: 'generated' | TheatreId) => {
    if (!isTheatreId(map)) return patch({ map });
    const alliances = THEATRES[map].alliances;
    patch({
      map,
      you: 0,
      season: THEATRES[map].season,
      nationTeams: alliances ? [...alliances] : [0, 1, 2, 3, 4, 5],
      teams: alliances ? 'custom' : 'ffa',
    });
  };

  const setOpp = (i: number, p: Partial<Opponent>) =>
    patch({ opponents: cfg.opponents.map((o, k) => (k === i ? { ...o, ...p } : o)) });

  const launch = () => {
    const seed = cfg.seedMode === 'random' ? Math.floor(Math.random() * 0x7fffffff) : cfg.seed;
    if (theatre) {
      start(
        {
          seed,
          mapSize: theatre.mapSize,
          theatre: theatre.id,
          factions: nations.map((_, i) =>
            i === you
              ? { controller: 'human', team: cfg.teams === 'ffa' ? i : nationTeam(i) }
              : { controller: 'ai', aiDifficulty: levelOf(i), team: cfg.teams === 'ffa' ? i : nationTeam(i) },
          ),
          dayLimit: cfg.dayLimit,
          vpVictoryShare: cfg.vpShare,
          startingTechs: TECH_PRESET_TECHS[cfg.techs] ?? [],
          ...(cfg.season !== 'random' ? { season: cfg.season } : {}),
        },
        cfg.speed,
      );
      return;
    }
    const factions: FactionSetup[] = [
      { controller: 'human', team: cfg.teams === 'ffa' ? 0 : cfg.myTeam },
      ...cfg.opponents.map((o, i) => ({
        controller: 'ai' as const,
        aiDifficulty: o.difficulty,
        team: cfg.teams === 'ffa' ? i + 1 : o.team,
      })),
    ];
    const config: SessionConfig = {
      seed,
      mapSize: cfg.mapSize,
      factions,
      dayLimit: cfg.dayLimit,
      vpVictoryShare: cfg.vpShare,
      startingTechs: TECH_PRESET_TECHS[cfg.techs] ?? [],
      ...(cfg.season !== 'random' ? { season: cfg.season } : {}),
    };
    start(config, cfg.speed);
  };

  const sizeInfo = sizes.find((m) => m.value === cfg.mapSize)!;
  const alignment =
    cfg.teams === 'ffa'
      ? t('sp.ffa')
      : [...teamsUsed]
          .sort()
          .map((team) => `${teamList.filter((x) => x === team).length}`)
          .reduce((a, b) => t('sp.brief.versus', { a, b }));
  const theatreMap = theatre ? loadTheatreMap(theatre.id) : null;

  return (
    <ScreenFrame
      kicker={t('sp.kicker')}
      title={t('sp.title')}
      footer={
        <>
          <span className={s.note}>{t('sp.footer')}</span>
          <span className={s.spacer} />
          <Button variant="ghost" onClick={() => setCfg(DEFAULT_SETUP)}>
            {t('ui.reset')}
          </Button>
        </>
      }
    >
      <div className={s.cols}>
        <div className={s.stack}>
          <section className={s.section}>
            <Rule>{t('sp.theatre')}</Rule>
            <Field label={t('sp.map')} hint={t('sp.map.hint')}>
              <Select
                value={theatre ? theatre.id : 'generated'}
                onChange={(v) => chooseMap(v as 'generated' | TheatreId)}
                options={mapOptions(t)}
              />
            </Field>
            {theatre && theatreMap ? (
              <div className={s.theatreCard}>
                <TheatrePreview id={theatre.id} width={220} height={170} label={t('sp.map.preview')} />
                <div className={s.theatreText}>
                  <span className={s.sizeName}>{theatreTitle(theatre.name, t.locale)}</span>
                  <span className={s.sizeMeta}>
                    {t('sp.map.meta', {
                      year: theatre.year,
                      w: theatreMap.width,
                      h: theatreMap.height,
                      km: t.num(theatreMap.hexKm ?? 10, 0),
                    })}
                  </span>
                  <span className={s.note}>{t(`theatre.${theatre.id}.desc`)}</span>
                  <span className={s.note}>{t('sp.map.neutral')}</span>
                </div>
              </div>
            ) : (
              <div className={s.sizeCards}>
                {sizes.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    className={cx(s.sizeCard, cfg.mapSize === m.value && s.sizeOn)}
                    onClick={() => patch({ mapSize: m.value })}
                  >
                    <MapSizeGlyph size={m.value} />
                    <span>
                      <span className={s.sizeName}>{m.label}</span>
                      <span className={s.sizeMeta}>
                        {m.meta} · {m.players}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div className={s.grid2}>
              <Field label={t('sp.seed')} hint={t('sp.seed.hint')}>
                <div className={s.row}>
                  <Segmented
                    value={cfg.seedMode}
                    onChange={(v) => patch({ seedMode: v })}
                    options={[
                      { value: 'random', label: t('ui.random') },
                      { value: 'fixed', label: t('ui.fixed') },
                    ]}
                  />
                  <TextInput
                    mono
                    value={String(cfg.seed)}
                    disabled={cfg.seedMode === 'random'}
                    onChange={(v) =>
                      patch({ seed: Math.max(0, Math.min(0x7fffffff, Number(v.replace(/\D/g, '')) || 0)) })
                    }
                    style={{ width: 120 }}
                    aria-label={t('sp.seed.aria')}
                  />
                  <Button
                    icon="dice"
                    title={t('sp.seed.roll')}
                    disabled={cfg.seedMode === 'random'}
                    onClick={() => patch({ seed: Math.floor(Math.random() * 999_999) })}
                  />
                </div>
              </Field>
              <Field label={t('sp.speed')} hint={tempo.find((x) => x.value === cfg.speed)?.title}>
                <Segmented value={cfg.speed} onChange={(v) => patch({ speed: v })} options={tempo} block />
              </Field>
            </div>
            <Field label={t('sp.season')} hint={seasons.find((x) => x.value === cfg.season)?.title}>
              <Segmented block value={cfg.season} onChange={(v) => patch({ season: v })} options={seasons} />
            </Field>
          </section>

          <section className={s.section}>
            <Rule>{t('sp.belligerents')}</Rule>
            <div className={s.row} style={{ justifyContent: 'space-between' }}>
              <Segmented
                value={cfg.teams}
                onChange={(v) => patch({ teams: v })}
                options={[
                  { value: 'ffa', label: t('sp.ffa') },
                  { value: 'custom', label: t('sp.customTeams') },
                ]}
              />
              <span className={s.note}>{t('sp.alliesNote')}</span>
            </div>
            {theatre ? (
              <div className={s.bellList}>
                {nations.map((n, i) => (
                  <div className={s.bell} key={n.name}>
                    <span className={s.swatch} style={{ background: cssColor(n.color) }} />
                    <span className={s.bellName}>
                      <span className={cx(s.bellTitle, i === you && s.you)}>{nationName(n, t.locale)}</span>
                      <span className={s.bellSub}>
                        {i === you
                          ? t('sp.nation.you')
                          : t('sp.opponentSub', {
                              difficulty: levels.find((d) => d.value === levelOf(i))?.title ?? '',
                            })}
                      </span>
                    </span>
                    {i === you ? (
                      <span />
                    ) : (
                      <Segmented
                        small
                        value={levelOf(i)}
                        onChange={(v) => patch({ levels: setAt(cfg.levels, i, v, 'veteran') })}
                        options={levels}
                      />
                    )}
                    {cfg.teams === 'custom' ? (
                      <Select
                        small
                        value={nationTeam(i)}
                        options={teams}
                        onChange={(v) => patch({ nationTeams: setAt(cfg.nationTeams, i, v, 0) })}
                      />
                    ) : (
                      <span />
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="flag"
                      title={t('sp.nation.takeHint')}
                      disabled={i === you}
                      onClick={() => patch({ you: i })}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <>
                <div className={s.bellList}>
                  <div className={s.bell}>
                    <span className={s.swatch} style={{ background: cssColor(FACTION_COLORS[0]) }} />
                    <span className={s.bellName}>
                      <span className={cx(s.bellTitle, s.you)}>{t('sp.you')}</span>
                      <span className={s.bellSub}>{t('sp.youSub')}</span>
                    </span>
                    <span />
                    {cfg.teams === 'custom' ? (
                      <Select
                        small
                        value={cfg.myTeam}
                        options={teams}
                        onChange={(v) => patch({ myTeam: v })}
                      />
                    ) : (
                      <span />
                    )}
                    <span />
                  </div>
                  {cfg.opponents.map((o, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: the slot index IS the identity (faction II, III …)
                    <div className={s.bell} key={i}>
                      <span className={s.swatch} style={{ background: cssColor(FACTION_COLORS[i + 1]) }} />
                      <span className={s.bellName}>
                        <span className={s.bellTitle}>{t('sp.opponent', { num: ROMAN[i + 1] })}</span>
                        <span className={s.bellSub}>
                          {t('sp.opponentSub', {
                            difficulty: levels.find((d) => d.value === o.difficulty)?.title ?? '',
                          })}
                        </span>
                      </span>
                      <Segmented
                        small
                        value={o.difficulty}
                        onChange={(v) => setOpp(i, { difficulty: v })}
                        options={levels}
                      />
                      {cfg.teams === 'custom' ? (
                        <Select
                          small
                          value={o.team}
                          options={teams}
                          onChange={(v) => setOpp(i, { team: v })}
                        />
                      ) : (
                        <span />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        icon="close"
                        title={t('sp.removeOpponent')}
                        disabled={cfg.opponents.length <= 1}
                        onClick={() => patch({ opponents: cfg.opponents.filter((_, k) => k !== i) })}
                      />
                    </div>
                  ))}
                </div>
                <div className={s.row}>
                  <Button
                    size="sm"
                    icon="plus"
                    disabled={cfg.opponents.length >= 5}
                    onClick={() =>
                      patch({
                        opponents: [
                          ...cfg.opponents,
                          { difficulty: 'veteran', team: Math.min(5, cfg.opponents.length + 1) },
                        ],
                      })
                    }
                  >
                    {t('sp.addOpponent')}
                  </Button>
                  <span className={s.note}>{t('sp.factionCount', { n: factionCount })}</span>
                </div>
              </>
            )}
            <div className={s.row}>
              {teamsInvalid && (
                <span className={s.warn}>
                  <Icon name="flag" size={14} /> {t('sp.oneTeam')}
                </span>
              )}
            </div>
          </section>

          <section className={s.section}>
            <Rule>{t('sp.rules')}</Rule>
            <div className={s.grid3}>
              <Field label={t('sp.dayLimit')} hint={t('sp.dayLimit.hint')}>
                <Segmented
                  block
                  value={cfg.dayLimit}
                  onChange={(v) => patch({ dayLimit: v })}
                  options={DAY_LIMITS.map((d) => ({ value: d, label: d ? String(d) : t('ui.none') }))}
                />
              </Field>
              <Field label={t('sp.decisive', { pct: t.pct(cfg.vpShare) })} hint={t('sp.decisive.hint')}>
                <Slider
                  value={cfg.vpShare}
                  min={0.5}
                  max={1}
                  step={0.05}
                  onChange={(v) => patch({ vpShare: v })}
                  label={t('sp.decisive.aria')}
                />
              </Field>
              <Field label={t('sp.techs')} hint={presets.find((x) => x.value === cfg.techs)?.title}>
                <Segmented block value={cfg.techs} onChange={(v) => patch({ techs: v })} options={presets} />
              </Field>
            </div>
          </section>
        </div>

        <aside>
          <Paper className={s.briefing}>
            <div className={s.briefHead}>
              <span>{t('sp.brief.head')}</span>
              <span>{t('sp.brief.no', { n: cfg.seedMode === 'fixed' ? cfg.seed : '—' })}</span>
            </div>
            <Stamp className={s.briefStamp} rotate={-11}>
              {t('sp.brief.stamp')}
            </Stamp>
            <div className={s.briefTitle}>{t('sp.brief.title')}</div>
            <div className={s.briefRows}>
              <span className={s.briefKey}>{t('sp.brief.theatre')}</span>
              <span className={s.briefVal}>
                {theatre
                  ? theatreTitle(theatre.name, t.locale)
                  : `${sizeInfo.label} ${sizeInfo.meta.replace(/ \S+$/, '')}`}
              </span>
              <span className={s.briefKey}>{t('sp.brief.season')}</span>
              <span className={s.briefVal}>
                {seasons.find((x) => x.value === cfg.season)?.label ?? t('season.random')}
              </span>
              <span className={s.briefKey}>{t('sp.brief.belligerents')}</span>
              <span className={s.briefVal}>
                {t('sp.brief.belligerentsVal', { n: factionCount, ai: factionCount - 1 })}
              </span>
              <span className={s.briefKey}>{t('sp.brief.alignment')}</span>
              <span className={s.briefVal}>{alignment}</span>
              <span className={s.briefKey}>{t('sp.brief.victory')}</span>
              <span className={s.briefVal}>{t('sp.brief.victoryVal', { pct: t.pct(cfg.vpShare) })}</span>
              <span className={s.briefKey}>{t('sp.brief.timeLimit')}</span>
              <span className={s.briefVal}>{dayLimitLabel(t, cfg.dayLimit)}</span>
              <span className={s.briefKey}>{t('sp.brief.technology')}</span>
              <span className={s.briefVal}>{presets.find((x) => x.value === cfg.techs)?.label}</span>
              <span className={s.briefKey}>{t('sp.brief.tempo')}</span>
              <span className={s.briefVal}>
                {t('speed.label', { v: tempo.find((x) => x.value === cfg.speed)?.label ?? '' })}
              </span>
              <span className={s.briefKey}>{t('sp.brief.opposition')}</span>
              <span className={s.briefVal}>
                {(theatre
                  ? nations.flatMap((_, i) => (i === you ? [] : [levelOf(i)]))
                  : cfg.opponents.map((o) => o.difficulty)
                )
                  .map((d) => t(`difficulty.${d}`)[0].toUpperCase())
                  .join(' · ')}
              </span>
            </div>
            <p className={s.briefNote}>
              {t('sp.brief.note', {
                teams:
                  cfg.teams === 'ffa'
                    ? t('sp.brief.noTeams')
                    : [...teamsUsed]
                        .sort()
                        .map((team) => TEAM_NAMES[team])
                        .join(', '),
              })}
            </p>
            <div className={s.briefSign}>
              <span>{t('sp.brief.approved')}</span>
              <span className={s.signature}>{t('sp.brief.signature')}</span>
            </div>
          </Paper>
          <div className={s.launch}>
            <Button
              variant="primary"
              size="lg"
              block
              disabled={teamsInvalid || launching}
              onClick={launch}
              icon="flag"
            >
              {launching ? t('sp.launching') : t('sp.launch')}
            </Button>
            {timedOut && (
              <div className={s.error} style={{ marginTop: 12 }}>
                <Icon name="close" />
                <span>{t('sp.timeout')}</span>
              </div>
            )}
          </div>
        </aside>
      </div>
    </ScreenFrame>
  );
}
